import { useState } from "react";
import { Box, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useFloatingBanners } from "@/features/banner/api/useFloatingBanners";
import { isSafeHttpUrl } from "@/utils/bannerUrl";
import { AdSlot } from "@/components/AdSlot";

/**
 * 画面右下のフローティング広告（300×250 レクタングル）。
 * マイページ限定でマウントする。AIサポートボタン(bottom:24px)と重ならないよう
 * bottom:88px に配置し、z-index はモーダル層より下・AIサポートより低くする。
 * ×=当日非表示(hidden)、「今後表示しない」=恒久非表示(dismissed)。
 */
export const FloatingBanner: React.FC = () => {
  const { banners, recordAction, removeBannerFromCache } =
    useFloatingBanners("floating");
  const [hiddenIds, setHiddenIds] = useState<Set<number>>(new Set());

  const visibleBanners = banners.filter(
    (b) =>
      !hiddenIds.has(b.id) &&
      isSafeHttpUrl(b.imageUrl) &&
      isSafeHttpUrl(b.linkUrl)
  );
  const banner = visibleBanners[0] ?? null;

  if (!banner) return null;

  const hideOptimistically = (id: number): void => {
    setHiddenIds((prev) => new Set(prev).add(id));
  };
  const restore = (id: number): void => {
    setHiddenIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleClose = (): void => {
    const id = banner.id;
    hideOptimistically(id);
    recordAction(id, "hidden")
      // 成功時はキャッシュからも除外し、画面再訪時の復活を防ぐ。
      .then(() => {
        removeBannerFromCache(id);
      })
      .catch(() => {
        restore(id);
      });
  };

  const handleDismiss = (): void => {
    const id = banner.id;
    hideOptimistically(id);
    recordAction(id, "dismissed")
      .then(() => {
        removeBannerFromCache(id);
      })
      .catch(() => {
        restore(id);
      });
  };

  return (
    <Box
      sx={{
        position: "fixed",
        // AIサポート(bottom:24px)の上。モーダル層(1300)より下・AIサポートより低く。
        zIndex: 1100,
        bottom: 88,
        right: 24,
        // 300×250 が画面に収まらない狭幅では出さない（SP等）
        display: { xs: "none", md: "block" },
      }}
    >
      <Box sx={{ position: "relative" }}>
        <IconButton
          aria-label="閉じる"
          onClick={handleClose}
          size="small"
          sx={{
            // 画像の右上角にわずかに外側へはみ出して重ねる（標準的な閉じるボタン位置）。
            position: "absolute",
            top: -4,
            right: -5,
            zIndex: 1,
            width: 24,
            height: 24,
            color: "#fff",
            bgcolor: "rgba(0,0,0,0.6)",
            "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
          }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
        {/* banner切替時に impression_id・計測状態を確実に初期化するため key で再マウント */}
        <AdSlot
          key={banner.id}
          banner={banner}
          position="floating"
          width={300}
          height={250}
          showPrLabel={false}
          contentPadding={0}
          imageSx={{ borderRadius: 0 }}
        />
        <Box
          component="button"
          type="button"
          onClick={handleDismiss}
          sx={{
            display: "block",
            width: "100%",
            mt: 0.5,
            p: 0,
            background: "none",
            border: "none",
            color: "rgba(0,0,0,0.6)",
            fontSize: 12,
            textDecoration: "underline",
            textAlign: "right",
            cursor: "pointer",
          }}
        >
          今後表示しない
        </Box>
      </Box>
    </Box>
  );
};
